<script setup>
import { onMounted, ref, computed, defineProps } from 'vue';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { utcToLocal } from '@/tools/TimeFunctions';

const props = defineProps({
  widget: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  }
});

const sourceNotFound = computed(() => {
  if (!props.widget.specifications) return null;
  const sources = Object.keys(props.widget.specifications);
  const hit = sources.find(s => props.sourceErrors?.[s]?.type === 'not_found');
  return hit ?? null;
});

const statDetails = computed(() => ({
  topic: props.widget.title,
  specifications: props.widget.specifications || {}
}));

const statInfo = ref(new Map());
const date = ref('');

onMounted(() => {
  for (let subcollection in statDetails.value.specifications) {
    grabData(subcollection);
  }
});

const grabData = (subcollection) => {
  const subcollectionRef = collection(db, 'data', props.dataID, subcollection);
  const q = query(subcollectionRef, orderBy('messageUTC', 'desc'), limit(1));
  onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      const data = change.doc.data();
      for (let obj of statDetails.value.specifications[subcollection]) {
        if (obj.field) {
          let value = data[obj.field];
          if (typeof value === 'string') {
            value = value.replace('FloLive', '');
          }
          const formattedValue = typeof value === 'number'
            ? value.toFixed(obj.decimals || 0) + ' ' + (obj.unit || '')
            : value;
          statInfo.value.set(obj.friendlyName || obj.field, formattedValue);
        } else if (obj.calculate) {
          const { formula, denominator } = obj.calculate;
          const variables = {};
          denominator.forEach((field) => {
            variables[field] = data[field];
          });
          const calculateFn = new Function(...denominator, `return ${formula}`);
          statInfo.value.set(
            obj.friendlyName,
            calculateFn(...Object.values(variables)).toFixed(obj.decimals || 0) + ' ' + (obj.unit || '')
          );
        }
      }
      date.value = 'Last Update: ' + utcToLocal(data.messageUTC, props.timeFormat);
    });
  });
};
</script>

<template>
  <div class="flex bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden shadow-mc-sm text-left flex-col h-full">
    <div class="h-[3px] bg-mc-red shrink-0"></div>
    <div class="px-4 pt-3 pb-2 border-b border-mc-steel dark:border-line-on-dark">
      <h1 class="font-display font-extrabold text-[15px] leading-tight text-mc-ink dark:text-on-dark m-0">{{ statDetails.topic }}</h1>
      <p class="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-muted dark:text-on-dark-muted mt-1">
        {{ sourceNotFound ? `Source "${sourceNotFound}" not found` : date }}
      </p>
    </div>
    <div class="py-3 px-4 bg-mc-white dark:bg-mc-ink flex-1">
      <p v-if="sourceNotFound" class="font-display font-extrabold text-2xl text-ink-muted dark:text-on-dark-muted text-center">N/A</p>
      <p v-else v-for="[detail, value] in statInfo" :key="detail" class="text-sm text-mc-ink dark:text-on-dark">
        <span class="font-semibold">{{ detail }}: </span>{{ value }}
      </p>
    </div>
  </div>
</template>
